import { useEmitEvent } from '@mntn/event-client/ui'
import { type ButtonSize, cn, Icon, Text } from '@mntn/ui-components'
import { type ComponentProps, forwardRef, useState } from 'react'
import type {
  CreateCampaignExperimentInput,
  CreateCampaignInput,
  OrderItem,
} from '@/flight-deck-api/services/order-items.service'
import { useAuthenticatedSession } from '@/providers/session'
import { Button, type FlightDeckButtonProps } from './button'
import { CreateExperimentDialog } from './create-experiment-action'
import { Tooltip } from './tooltip'
import { type CampaignCreationIneligibilityReason, checkCanCreateCampaign } from './utils'

type Props = Omit<FlightDeckButtonProps, 'children' | 'disabled' | 'onClick' | 'size' | 'variant'> & {
  orderItem: OrderItem
  handleClick: () => void
  label?: string
  buttonSize?: ButtonSize
  isLoading?: boolean
  className?: string
  isDisabled?: boolean
  variant?: FlightDeckButtonProps['variant']
}

export const CreateCampaignButton = forwardRef<HTMLButtonElement, Props>(function CreateCampaignButton(
  {
    orderItem,
    handleClick,
    label = 'Create Campaign',
    buttonSize = 'tiny',
    className,
    isLoading = false,
    isDisabled = false,
    variant = 'screen',
    ...buttonProps
  },
  ref,
) {
  const eligibility = checkCanCreateCampaign({
    airEndTime: orderItem.airEndTime,
    unallocatedImpressions: orderItem.impressionMetrics.unallocated,
  })

  const emitEvent = useEmitEvent()

  const onClick = () => {
    emitEvent({
      name: 'create campaign button clicked',
      request: {
        orderItemId: orderItem.orderItemId,
      },
    })

    handleClick()
  }

  const button = (
    <Button
      {...buttonProps}
      ref={ref}
      variant={variant}
      size={buttonSize}
      className={cn('shadow-lg disabled:opacity-40', className)}
      onClick={onClick}
      disabled={!eligibility.canCreateCampaign || isDisabled || isLoading}
      data-intercom-target={`create-campaign-${eligibility.canCreateCampaign ? 'enabled' : 'disabled'}`}
    >
      {isLoading ? <Icon name="LoaderCircle" className="w-5 h-5 animate-spin text-header-inverse" /> : label}
    </Button>
  )

  if (eligibility.canCreateCampaign) return button

  const { title, message } = INELIGIBILITY_TOOLTIP_CONTENT[eligibility.reason]

  return (
    <Tooltip
      // When the button is disabled, it can't receive pointer events,
      // which breaks Radix Tooltip's trigger behavior. Wrapping it in a <span>
      // lets the tooltip still show on hover without appearing automatically on render.
      trigger={<span className="inline-block">{button}</span>}
      content={
        <div className="p-3">
          <Text variant="h6" className="text-header-inverse">
            {title}:
          </Text>
          <Text variant="base-sm" className="text-header-inverse">
            {message}
          </Text>
        </div>
      }
      variant="dark"
    />
  )
})

export function CreateCampaignOrExperimentButton({
  onCreateCampaign,
  onCreateExperiment,
  orderItem,
  ...buttonProps
}: Pick<
  ComponentProps<typeof CreateCampaignButton>,
  'buttonSize' | 'className' | 'isDisabled' | 'isLoading' | 'variant'
> & {
  onCreateCampaign: (input: CreateCampaignInput) => Promise<void>
  onCreateExperiment: (input: CreateCampaignExperimentInput) => Promise<void>
  orderItem: OrderItem
}) {
  const { session } = useAuthenticatedSession()
  const [isExperimentDialogOpen, setIsExperimentDialogOpen] = useState(false)
  const canCreateExperiment = session.loginAs === 'admin' && orderItem.isTest
  const isTest = session.loginAs === 'demo'

  if (!canCreateExperiment) {
    return (
      <CreateCampaignButton {...buttonProps} handleClick={() => onCreateCampaign({ isTest })} orderItem={orderItem} />
    )
  }

  return (
    <>
      <CreateCampaignButton
        {...buttonProps}
        handleClick={() => setIsExperimentDialogOpen(true)}
        label="Create Experiment"
        orderItem={orderItem}
      />
      <CreateExperimentDialog
        open={isExperimentDialogOpen}
        onOpenChange={setIsExperimentDialogOpen}
        onSubmit={clonedFromCampaignGroupId => onCreateExperiment({ clonedFromCampaignGroupId })}
      />
    </>
  )
}

const INELIGIBILITY_TOOLTIP_CONTENT: Record<
  CampaignCreationIneligibilityReason,
  {
    title: string
    message: string
  }
> = {
  expired: { title: 'Expired', message: 'Campaigns can no longer be created' },
  fullyAllocated: {
    title: 'Fully allocated',
    message: 'No more campaigns needed',
  },
}
